import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})

export class IDBPhotoCacheService {

  private readonly DB_NAME = 'photo_cache_db';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'high_quality_urls';

  constructor() {
    // Initialize the database
    this.initializeDatabase();
  }

  /**
   * Initialize the IndexedDB database
   */
  private async initializeDatabase(): Promise<void> {
    try {
      const db = await this.openDatabase();
      //console.log('[PhotoCacheService] Database initialized successfully');
      db.close();
    } catch (error) {
      console.error('[PhotoCacheService] Error initializing database:', error);
    }
  }

  /**
   * Opens the database and returns the IDBDatabase object
   */
  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onupgradeneeded = (event) => {
        // console.log('[PhotoCacheService] Database upgrade needed, creating stores');
        const db = request.result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          //console.log(`[PhotoCacheService] Created object store: ${this.STORE_NAME}`);
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        console.error('[PhotoCacheService] Error opening database:', event);
        reject(event);
      };
    });
  }

  /**
   * Get a high-quality URL for a photo from cache,
   * or returns the original URL if not found
   * 
   * @param photoId The ID of the photo
   * @param originalUrl The original URL of the photo
   * @returns The high-quality URL from cache or the original URL
   */
  async getHighQualityUrl(photoId: string, originalUrl: string): Promise<string> {
    try {
      // Try to get cached URL from IDB
      const cachedUrl = await this.getUrlFromCache(photoId);
      if (cachedUrl) {
        // console.log('[PhotoCacheService] Using cached high-quality URL for photo:', photoId);
        return cachedUrl;
      }

      // If not in cache, use original URL and store it for future use
      if (originalUrl && !originalUrl.startsWith('data:')) {
        // console.log('[PhotoCacheService] Storing original URL in cache for future use:', photoId);
        await this.storeUrl(photoId, originalUrl);
      }

      return originalUrl;
    } catch (error) {
      console.warn('[PhotoCacheService] Error getting high-quality URL:', error);
      return originalUrl; // Fallback to original URL
    }
  }

  /**
   * Get a URL from the cache by photo ID
   * 
   * @param photoId The ID of the photo
   * @returns The cached URL or null if not found
   */
  async getUrlFromCache(photoId: string): Promise<string | null> {
    try {
      const db = await this.openDatabase();

      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction([this.STORE_NAME], 'readonly');
          const store = transaction.objectStore(this.STORE_NAME);
          const request = store.get(photoId);

          request.onsuccess = () => {
            db.close();
            if (request.result) {
              resolve(request.result.url);
            } else {
              resolve(null);
            }
          };

          request.onerror = (event) => {
            db.close();
            console.error('[PhotoCacheService] Error reading from cache:', event);
            reject(event);
          };

          transaction.oncomplete = () => {
            db.close();
          };
        } catch (error) {
          db.close();
          console.error('[PhotoCacheService] Error in transaction:', error);
          reject(error);
        }
      });
    } catch (error) {
      console.error('[PhotoCacheService] Error opening database for read:', error);
      return null;
    }
  }



  async storeUrl(photoId: string, url: string): Promise<void> {
    if (!photoId || !url) {
      console.error('[PhotoCacheService] Cannot store URL: Invalid photoId or URL');
      throw new Error('Invalid photoId or URL');
    }

    //console.log(`[PhotoCacheService] Storing URL for photo ${photoId}`);

    try {
      const db = await this.openDatabase();

      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction([this.STORE_NAME], 'readwrite');
          const store = transaction.objectStore(this.STORE_NAME);

          const entry = {
            id: photoId,
            url: url,
            timestamp: Date.now()
          };

          const request = store.put(entry);

          request.onsuccess = () => {
            //console.log(`[PhotoCacheService] Successfully stored URL for photo ${photoId}`);
            // Close DB explicitly after successful operation
            setTimeout(() => {
              db.close();
              resolve();
            }, 0);
          };

          request.onerror = (event) => {
            console.error(`[PhotoCacheService] Error storing URL for photo ${photoId}:`, event);
            db.close();
            reject(new Error(`Failed to store URL: ${request.error?.message || 'Unknown error'}`));
          };

          // Make sure transaction errors are caught
          transaction.onerror = (event) => {
            console.error(`[PhotoCacheService] Transaction error while storing URL:`, event);
            db.close();
            reject(new Error('Transaction error'));
          };

          // Ensure completion callback
          transaction.oncomplete = () => {
            //console.log(`[PhotoCacheService] Transaction completed for storing URL`);
            db.close();
            resolve();
          };
        } catch (error) {
          console.error(`[PhotoCacheService] Exception in store operation:`, error);
          db.close();
          reject(error);
        }
      });
    } catch (error) {
      console.error(`[PhotoCacheService] Failed to open database for storing:`, error);
      throw error;
    }
  }

  /**
   * Remove a URL from the cache
   * 
   * @param photoId The ID of the photo to remove
   */
  async removeUrl(photoId: string): Promise<void> {
    try {
      const db = await this.openDatabase();

      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction([this.STORE_NAME], 'readwrite');
          const store = transaction.objectStore(this.STORE_NAME);
          const request = store.delete(photoId);

          request.onsuccess = () => {
            // console.log('[PhotoCacheService] URL removed successfully for photo:', photoId);
            resolve();
          };

          request.onerror = (event) => {
            console.error('[PhotoCacheService] Error removing URL:', event);
            reject(event);
          };

          transaction.oncomplete = () => {
            db.close();
          };
        } catch (error) {
          db.close();
          console.error('[PhotoCacheService] Error in transaction:', error);
          reject(error);
        }
      });
    } catch (error) {
      console.error('[PhotoCacheService] Error opening database for delete:', error);
    }
  }

  /**
   * Clear all cached URLs older than the specified age
   * 
   * @param maxAgeMs Maximum age in milliseconds (default: 7 days)
   */
  async clearOldEntries(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    try {
      const db = await this.openDatabase();
      const cutoffTime = Date.now() - maxAgeMs;

      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction([this.STORE_NAME], 'readwrite');
          const store = transaction.objectStore(this.STORE_NAME);
          const request = store.openCursor();

          let deletedCount = 0;

          request.onsuccess = (event) => {
            const cursor = request.result;
            if (cursor) {
              if (cursor.value.timestamp < cutoffTime) {
                // Delete old entries
                cursor.delete();
                deletedCount++;
              }
              cursor.continue();
            }
          };

          transaction.oncomplete = () => {
            // console.log(`[PhotoCacheService] Cleaned up ${deletedCount} old cached URLs`);
            db.close();
            resolve();
          };

          transaction.onerror = (event) => {
            console.error('[PhotoCacheService] Error clearing old entries:', event);
            db.close();
            reject(event);
          };
        } catch (error) {
          db.close();
          console.error('[PhotoCacheService] Error in transaction:', error);
          reject(error);
        }
      });
    } catch (error) {
      console.error('[PhotoCacheService] Error opening database for cleanup:', error);
    }
  }
}
